import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PageHero from '../components/common/PageHero';
import { ChevronDown, ChevronUp, Crown, Calendar, Users, BookOpen, FileCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import AdSlot from '../components/ads/AdSlot';

const Dashboard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [expandedSections, setExpandedSections] = useState({
    doctors: true,
    students: true,
    executive: false,
    myCurrentEvents: false,
    currentEvents: false,
    myUpcomingEvents: false,
    upcomingEvents: false,
    myCompletedEvents: false,
    completedEvents: false
  });
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [students, setStudents] = useState([]);
  const [events, setEvents] = useState({
    my_current_events: [],
    current_events: [],
    my_upcoming_events: [],
    upcoming_events: [],
    my_completed_events: [],
    completed_events: []
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all dashboard data in parallel
      const [statsRes, doctorsRes, studentsRes, eventsRes] = await Promise.all([
        axios.get(`${backendUrl}/api/dashboard/stats`),
        axios.get(`${backendUrl}/api/members/doctors?limit=50`),
        axios.get(`${backendUrl}/api/members/students?limit=50`),
        axios.get(`${backendUrl}/api/dashboard/events`)
      ]);
      
      setStats(statsRes.data);
      setDoctors(doctorsRes.data.members || []);
      setStudents(studentsRes.data.members || []);
      setEvents(eventsRes.data);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const CollapsibleSection = ({ title, isExpanded, onToggle, children, headerColor = 'bg-[#2c3e7d]' }) => (
    <Card className="mb-4">
      <CardHeader 
        className={`${headerColor} text-white cursor-pointer flex flex-row items-center justify-between p-4`}
        onClick={onToggle}
      >
        <CardTitle className="text-lg font-semibold">{title}</CardTitle>
        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </CardHeader>
      {isExpanded && (
        <CardContent className="p-4">
          {children}
        </CardContent>
      )}
    </Card>
  );

  const StatCard = ({ title, value, icon: Icon, color = 'text-[#2c3e7d]' }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-1">{title}</p>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
          </div>
          <Icon className={`${color} h-12 w-12`} />
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHero title="Member Dashboard" />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHero title="Member Dashboard" />
      
      <div className="container mx-auto px-4 py-8">
        {/* Welcome Message */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.full_name}!
          </h2>
          <p className="text-gray-600">
            {user?.is_approved 
              ? 'Your membership is active. Explore the resources below.'
              : 'Your membership is pending approval. You\'ll have full access once approved.'}
          </p>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard 
              title="Total Doctors" 
              value={stats.total_doctors} 
              icon={Users}
              color="text-[#2c3e7d]"
            />
            <StatCard 
              title="Total Students" 
              value={stats.total_students} 
              icon={BookOpen}
              color="text-blue-600"
            />
            <StatCard 
              title="Total Events" 
              value={stats.total_events} 
              icon={Calendar}
              color="text-green-600"
            />
            <StatCard 
              title="My Registered Events" 
              value={stats.user_registered_events} 
              icon={FileCheck}
              color="text-orange-600"
            />
          </div>
        )}

        {/* Dashboard Ad Placement */}
        <div className="mb-8">
          <AdSlot
            placementCode="dashboard_top"
            className="w-full rounded-lg overflow-hidden shadow-md"
            placeholderVariant="hero"
            placeholderWidth="auto"
            placeholderHeight={200}
          />
        </div>

        {/* Membership Section */}
        <section className="mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">MEMBERSHIP</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Chirosport Doctors */}
            <CollapsibleSection
              title={`CHIROSPORT DOCTORS (${doctors.length})`}
              isExpanded={expandedSections.doctors}
              onToggle={() => toggleSection('doctors')}
            >
              {doctors.length === 0 ? (
                <p className="text-gray-600">No doctors found</p>
              ) : (
                <div className="space-y-3">
                  {doctors.map(doctor => (
                    <div key={doctor.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div>
                        <p className="font-medium text-gray-900">{doctor.full_name}</p>
                        <p className="text-sm text-gray-600">
                          {doctor.practice_name && `${doctor.practice_name} • `}
                          {doctor.city}, {doctor.province}
                        </p>
                        {doctor.ahpcsa_number && (
                          <p className="text-xs text-gray-500">AHPCSA: {doctor.ahpcsa_number}</p>
                        )}
                      </div>
                      <Crown className="text-yellow-500" size={24} />
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>

            {/* Chirosport Students */}
            <CollapsibleSection
              title={`CHIROSPORT STUDENTS (${students.length})`}
              isExpanded={expandedSections.students}
              onToggle={() => toggleSection('students')}
            >
              {students.length === 0 ? (
                <p className="text-gray-600">No students found</p>
              ) : (
                <div className="space-y-3">
                  {students.map(student => (
                    <div key={student.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div>
                        <p className="font-medium text-gray-900">{student.full_name}</p>
                        <p className="text-sm text-gray-600">
                          {student.practice_name && `${student.practice_name} • `}
                          {student.city}, {student.province}
                        </p>
                        {student.student_number && (
                          <p className="text-xs text-gray-500">Student #: {student.student_number}</p>
                        )}
                      </div>
                      <Crown className="text-blue-500" size={24} />
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>
          </div>
        </section>

        {/* Executive Member Section */}
        {user?.role === 'executive' || user?.role === 'admin' ? (
          <section className="mb-8">
            <CollapsibleSection
              title="EXECUTIVE MEMBER"
              isExpanded={expandedSections.executive}
              onToggle={() => toggleSection('executive')}
              headerColor="bg-red-600"
            >
              <p className="text-gray-600">Executive member content and resources will appear here.</p>
            </CollapsibleSection>
          </section>
        ) : null}

        {/* Newsletter Section */}
        <section className="mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">NEWSLETTER</h3>
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-600">Stay updated with the latest news and updates from Chirosport SA.</p>
              <button className="mt-4 bg-[#2c3e7d] text-white px-6 py-2 rounded-full hover:bg-[#1e2a5a] transition-colors">
                Subscribe to Newsletter
              </button>
            </CardContent>
          </Card>
        </section>

        {/* Events Section */}
        <section className="mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">EVENTS</h3>
          
          <div className="space-y-4">
            <CollapsibleSection
              title={`My Current Events (${events.my_current_events.length})`}
              isExpanded={expandedSections.myCurrentEvents}
              onToggle={() => toggleSection('myCurrentEvents')}
            >
              {events.my_current_events.length === 0 ? (
                <p className="text-gray-600">No Events Available</p>
              ) : (
                <div className="space-y-3">
                  {events.my_current_events.map(event => (
                    <div key={event.id} className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h4 className="font-semibold text-gray-900">{event.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{event.location} • {event.event_date}</p>
                      <p className="text-sm text-green-600 mt-2">✓ Registered</p>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>

            <CollapsibleSection
              title={`Current Events (${events.current_events.length})`}
              isExpanded={expandedSections.currentEvents}
              onToggle={() => toggleSection('currentEvents')}
            >
              {events.current_events.length === 0 ? (
                <p className="text-gray-600">No Events Available</p>
              ) : (
                <div className="space-y-3">
                  {events.current_events.map(event => (
                    <div key={event.id} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-semibold text-gray-900">{event.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{event.location} • {event.event_date}</p>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>

            <CollapsibleSection
              title={`My Upcoming Events (${events.my_upcoming_events.length})`}
              isExpanded={expandedSections.myUpcomingEvents}
              onToggle={() => toggleSection('myUpcomingEvents')}
            >
              {events.my_upcoming_events.length === 0 ? (
                <p className="text-gray-600">No Events Available</p>
              ) : (
                <div className="space-y-3">
                  {events.my_upcoming_events.map(event => (
                    <div key={event.id} className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h4 className="font-semibold text-gray-900">{event.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{event.location} • {event.event_date}</p>
                      <p className="text-sm text-green-600 mt-2">✓ Registered</p>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>

            <CollapsibleSection
              title={`Upcoming Events (${events.upcoming_events.length})`}
              isExpanded={expandedSections.upcomingEvents}
              onToggle={() => toggleSection('upcomingEvents')}
            >
              {events.upcoming_events.length === 0 ? (
                <p className="text-gray-600">No Events Available</p>
              ) : (
                <div className="space-y-3">
                  {events.upcoming_events.map(event => (
                    <div key={event.id} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-semibold text-gray-900">{event.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{event.location} • {event.event_date}</p>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>

            <CollapsibleSection
              title={`My Completed Events (${events.my_completed_events.length})`}
              isExpanded={expandedSections.myCompletedEvents}
              onToggle={() => toggleSection('myCompletedEvents')}
            >
              {events.my_completed_events.length === 0 ? (
                <p className="text-gray-600">No Events Available</p>
              ) : (
                <div className="space-y-3">
                  {events.my_completed_events.map(event => (
                    <div key={event.id} className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <h4 className="font-semibold text-gray-900">{event.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{event.location} • {event.event_date}</p>
                      <p className="text-sm text-gray-500 mt-2">✓ Attended</p>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>

            <CollapsibleSection
              title={`Completed Events (${events.completed_events.length})`}
              isExpanded={expandedSections.completedEvents}
              onToggle={() => toggleSection('completedEvents')}
            >
              {events.completed_events.length === 0 ? (
                <p className="text-gray-600">No Events Available</p>
              ) : (
                <div className="space-y-3">
                  {events.completed_events.map(event => (
                    <div key={event.id} className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <h4 className="font-semibold text-gray-900">{event.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{event.location} • {event.event_date}</p>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;